import { useState, useCallback, useRef, useEffect, createElement } from 'react';
import type { MapOverlayConfig, MapHoverConfig } from './types';
import { resolveAddressString } from './utils';
import { useGeocode } from './useGeocode';
import { useMediaQuery } from '../../../hooks/core/useMediaQuery';
import { MapOverlayPanel } from './MapOverlayPanel';
import { MapPreviewTooltip } from './MapPreviewTooltip';
import { useMapApiContext } from './MapOverlayProvider';

/**
 * A stable identity for "which place is this panel showing".
 *
 * Coordinates are included as well as the address because two different
 * targets can share an address string (a site and its office), and a change in
 * coordinates alone is still a different map.
 */
export function mapTargetKey(config: MapOverlayConfig): string {
  return `${resolveAddressString(config.address)}|${config.lat ?? ''}|${config.lng ?? ''}`;
}

export function useMapOverlay() {
  const { requestMapApi } = useMapApiContext();

  // Signal on mount that the Maps SDK is needed. This hook is only called on
  // pages/components that actually render map-capable UI, so this is the right
  // trigger point for lazy SDK loading.
  useEffect(() => {
    requestMapApi();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [panelConfig, setPanelConfig] = useState<MapOverlayConfig | null>(null);
  const [hoverConfig, setHoverConfig] = useState<MapHoverConfig | null>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Element that triggered the current hover preview — reused as the
  // `triggerRef` for the full map panel when the preview is expanded.
  const hoverTriggerElRef = useRef<HTMLElement | null>(null);

  // Preload geocoding for the hovered address
  useGeocode(
    hoverConfig?.address ?? null,
    hoverConfig?.lat,
    hoverConfig?.lng,
  );

  const openMap = useCallback((config: MapOverlayConfig) => {
    const addressStr = resolveAddressString(config.address);
    if (!addressStr.trim()) return;
    setPanelConfig(config);
    setHoverConfig(null);
  }, []);

  const closeMap = useCallback(() => {
    setPanelConfig(null);
    setHoverConfig(null);
    setTriggerRect(null);
  }, []);

  // Cancels any pending "hide the tooltip" timer. Shared by the trigger
  // element's onMouseEnter and the tooltip's own onMouseEnter, so moving the
  // mouse from the trigger down into the tooltip keeps it open.
  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  // Schedules the tooltip to hide after a short grace period. Shared by the
  // trigger element's onMouseLeave and the tooltip's own onMouseLeave.
  const scheduleHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setHoverConfig(null);
      setTriggerRect(null);
    }, 150);
  }, []);

  const hoverProps = useCallback((config: MapHoverConfig) => {
    if (isMobile) {
      return { onMouseEnter: undefined, onMouseLeave: undefined };
    }

    return {
      onMouseEnter: (e: React.MouseEvent) => {
        cancelHide();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        hoverTriggerElRef.current = e.currentTarget as HTMLElement;
        showTimerRef.current = setTimeout(() => {
          setHoverConfig(config);
          setTriggerRect(rect);
        }, 300);
      },
      onMouseLeave: () => {
        if (showTimerRef.current) {
          clearTimeout(showTimerRef.current);
          showTimerRef.current = null;
        }
        scheduleHide();
      },
    };
  }, [isMobile, cancelHide, scheduleHide]);

  // Expands the current hover preview into the full interactive map panel,
  // reusing the hovered element as the panel's anchor. No-ops if the caller
  // didn't supply an `expandConfig` for this hover target.
  const handleExpand = useCallback(() => {
    if (!hoverConfig?.expandConfig) return;
    const { expandConfig, address, lat, lng } = hoverConfig;
    openMap({
      ...expandConfig,
      address,
      lat: expandConfig.lat ?? lat,
      lng: expandConfig.lng ?? lng,
      triggerRef: hoverTriggerElRef,
    });
  }, [hoverConfig, openMap]);

  // Hover handlers for the tooltip itself, so hovering into it (to click
  // Expand/Directions) doesn't cause it to close underneath the cursor.
  const tooltipHoverProps = {
    onMouseEnter: cancelHide,
    onMouseLeave: scheduleHide,
  };

  // Dismiss hover tooltip on scroll (stale position)
  useEffect(() => {
    if (!hoverConfig) return;
    const onScroll = () => {
      setHoverConfig(null);
      setTriggerRect(null);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [hoverConfig]);

  // Clear any pending show/hide timers on unmount so a timeout firing after
  // the owning component (e.g. a LeadCard removed by a stage change) has
  // unmounted doesn't call setState on it.
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const isOpen = panelConfig !== null;

  // KEYED ON THE TARGET, and the key is load-bearing rather than cosmetic.
  // `openMap()` can be called while the panel is already open (the hover
  // preview's Expand does exactly that, and so does clicking a second
  // address), which swaps `panelConfig` WITHOUT remounting the subtree. The
  // error boundary now wrapping <Map> does not self-recover once tripped, so
  // without this key one bad address would leave every later address showing
  // "Map could not be displayed" for the rest of the open session. Keying on
  // the target makes a new address a new subtree, which is a fresh chance to
  // render.
  const MapPanel = isOpen
    ? createElement(MapOverlayPanel, {
      key: mapTargetKey(panelConfig),
      config: panelConfig,
      onClose: closeMap,
    })
    : null;

  const PreviewTooltip = hoverConfig && !isOpen
    ? createElement(MapPreviewTooltip, {
      config: hoverConfig,
      triggerRect,
      onExpand: hoverConfig.expandConfig ? handleExpand : undefined,
      ...tooltipHoverProps,
    })
    : null;

  return {
    openMap,
    closeMap,
    isOpen,
    hoverProps,
    MapPanel,
    PreviewTooltip,
  };
}
